import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Control, useFieldArray, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CalendarIcon, InfoCircle } from '../../../../assets/icons/index';
import { AnimatedNumber } from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { OverlayDatePicker as DatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { FormInput } from '../../../../components/input/index';
import { Stakeholder, useFormat, useLocale } from '../../../../hooks/index';
import { EventFormType } from '../../../../types/events.types';
import { toNumber } from '../../../../utils/to-number.util';
import { FormDropDown, ShareClassCombobox, StakeholderCombobox } from '../components/index';
import { FormSchema } from '../event-form.validation';

export type ClassConversionStepOneProps = {
  companyId: string;
  control: Control<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
};

export const ClassConversionStepOne: FC<ClassConversionStepOneProps> = ({
  control,
  setFormData,
  companyId,
}) => {
  const { messagesLocale } = useLocale();
  const { initialStep } = useWatch<FormSchema>({ control });

  const defaultDate = useMemo(() => new Date(+0), []);

  const {
    fields: classConversionItemsFields,
    append: classConversionItemsAppend,
    remove: classConversionItemsRemove,
  } = useFieldArray({
    control,
    name: 'stepOne.eventDetails.conversion.classConversionItems',
  });

  const handleRemoveClassConversionItems = useCallback(
    (index: number) => {
      classConversionItemsRemove(index);
    },
    [classConversionItemsRemove],
  );

  const handleAppendClassConversionItems = useCallback(() => {
    classConversionItemsAppend({
      date: defaultDate,
      balance: 0,
      stakeholder: { id: '', fullName: '' },
      shareClassFrom: { id: '', name: '', conversionRatio: 0 },
      shareClassTo: { id: '', name: '' },
      shares: 0,
    });
  }, [classConversionItemsAppend, defaultDate]);

  useEffect(() => {
    if (initialStep?.type === EventFormType.CONVERSION && classConversionItemsFields.length === 0) {
      handleAppendClassConversionItems();
    }
  }, [classConversionItemsFields.length, handleAppendClassConversionItems, initialStep?.type]);

  return (
    <>
      {classConversionItemsFields.map((field, index) => (
        <ClassConversionItem
          companyId={companyId}
          control={control}
          handleRemoveClassConversionItems={handleRemoveClassConversionItems}
          index={index}
          key={field.id}
          setFormData={setFormData}
          showRemoveButton={classConversionItemsFields.length > 1}
        />
      ))}
      <Button
        className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent"
        disabled={classConversionItemsFields.length >= 100}
        onClick={handleAppendClassConversionItems}
        styleType="NONE"
      >
        + <AppFormattedMessage id="ADD_ANOTHER_CLASS_CONVERSION" />
      </Button>
      <DropDown
        answer={messagesLocale['CLASS_CONVERSION_EVENT_EXPLANATION_1']}
        className="rounded-lg bg-gray-50"
        question={<AppFormattedMessage id="WHAT_IS_CLASS_CONVERSION" />}
      />
    </>
  );
};

const ClassConversionItem: FC<
  {
    handleRemoveClassConversionItems: (index: number) => void;
    index: number;
    showRemoveButton: boolean;
  } & ClassConversionStepOneProps
> = ({
  control,
  setFormData,
  companyId,
  index,
  handleRemoveClassConversionItems,
  showRemoveButton,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { format } = useFormat();
  const { messagesLocale } = useLocale();
  const defaultDate = new Date(+0);

  const stakeholderId =
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.stakeholder?.id || '';
  const shareClassId =
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shareClassFrom?.id || '';
  const initialShares =
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.initialShares || undefined;
  const initialStakeholderId =
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.initialStakeholderId;
  const initialShareClassFromId =
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.initialShareClassFromId;
  const initialShareClassToId =
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.initialShareClassToId;
  const shouldAddInitialShares =
    initialShares &&
    initialStakeholderId === stakeholderId &&
    initialShareClassFromId === shareClassId;
  const shouldSubtractInitialShares =
    initialShares &&
    initialStakeholderId === stakeholderId &&
    initialShareClassToId === shareClassId;

  const { balance } = Stakeholder.useBalance({ companyId, stakeholderId, shareClassId });

  useEffect(() => {
    if (balance) {
      setFormData(
        `stepOne.eventDetails.conversion.classConversionItems.${index}.balance`,
        shouldAddInitialShares
          ? Number(balance) + initialShares
          : shouldSubtractInitialShares
            ? Number(balance) - initialShares
            : Number(balance),
      );
    }
  }, [
    balance,
    index,
    initialShares,
    setFormData,
    shouldAddInitialShares,
    shouldSubtractInitialShares,
  ]);

  const getFullTitle = (index: number) => {
    const stakeholderName =
      stepOne?.eventDetails?.['conversion']?.classConversionItems?.[index]?.stakeholder?.fullName;

    if (stakeholderName) {
      return `${messagesLocale['CONVERSION']} - ${stakeholderName}`;
    }

    return messagesLocale['CONVERSION'];
  };

  const getConvertedShares = useCallback(
    (index: number) => {
      const shares = stepOne?.eventDetails?.['conversion']?.classConversionItems?.[index]?.shares;
      const conversionRatio =
        stepOne?.eventDetails?.['conversion']?.classConversionItems?.[index]?.shareClassFrom
          ?.conversionRatio;

      if (shares && conversionRatio) {
        return Number(shares * conversionRatio);
      }

      return 0;
    },
    [stepOne?.eventDetails],
  );

  const calculateBalance = useCallback(
    (index: number) => {
      if (balance) {
        const currentItem = stepOne?.eventDetails?.conversion?.classConversionItems?.[index];

        if (!currentItem) {
          return Number(balance);
        }

        const filteredItems =
          stepOne?.eventDetails?.conversion?.classConversionItems &&
          stepOne?.eventDetails?.conversion?.classConversionItems.filter((_, i) => i !== index);

        if (!filteredItems || filteredItems.length === 0) {
          return Number(balance);
        }

        const matchingItems = filteredItems.filter(
          (item) =>
            item.stakeholder?.id === currentItem.stakeholder?.id &&
            item.shareClassFrom?.id === currentItem.shareClassFrom?.id,
        );

        if (matchingItems.length === 0) {
          return Number(balance);
        }

        const totalShares = matchingItems.reduce((sum, item) => sum + toNumber(item.shares), 0);

        return Number(balance - totalShares);
      }
      return 0;
    },
    [balance, stepOne?.eventDetails?.conversion?.classConversionItems],
  );

  const calculateAvailableShares = useCallback(
    (index: number, shouldConvert: boolean) => {
      const conversionRatio =
        stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shareClassFrom
          ?.conversionRatio || 1;
      const shares = shouldConvert
        ? (stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shares || 0) *
          conversionRatio
        : stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shares || 0;
      const balance = shouldAddInitialShares
        ? calculateBalance(index) + (initialShares || 0)
        : shouldSubtractInitialShares
          ? calculateBalance(index) - (initialShares || 0)
          : calculateBalance(index);
      const convertedBalance = shouldConvert ? balance * conversionRatio : balance;

      return convertedBalance - shares;
    },
    [
      calculateBalance,
      initialShares,
      shouldAddInitialShares,
      shouldSubtractInitialShares,
      stepOne?.eventDetails?.conversion?.classConversionItems,
    ],
  );

  const isShareClassDuplicate =
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shareClassFrom?.id &&
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shareClassTo?.id &&
    stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shareClassFrom?.id ===
      stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shareClassTo?.id;

  return (
    <FormDropDown
      handleRemove={() => handleRemoveClassConversionItems(index)}
      showRemoveButton={showRemoveButton}
      title={getFullTitle(index)}
    >
      <div className="flex w-full flex-col gap-3">
        <div className="flex w-full items-center justify-between">
          <span className="text-sm font-[450] text-gray-700">
            {' '}
            <AppFormattedMessage id="EVENT_DATE" />
          </span>
          <DatePicker
            onSelect={(date) =>
              setFormData(
                `stepOne.eventDetails.conversion.classConversionItems.${index}.date`,
                date,
              )
            }
            value={stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.date}
            wrapperClassName="w-[224px]"
          >
            <FormInput
              autoComplete="off"
              control={control}
              icon={<CalendarIcon className="mr-2 size-6" iconColor="#98A2B3" />}
              name={`stepOne.eventDetails.conversion.classConversionItems.${index}.date`}
              placeholder={<AppFormattedMessage id="DATE" />}
              transform={(value) => {
                return value && new Date(value).getTime() !== defaultDate.getTime()
                  ? format(value, 'dd/MM/yyyy')
                  : '';
              }}
              wrapperClassName="w-full"
            />
          </DatePicker>
        </div>
        <div className="flex w-full items-center justify-between">
          <span className="text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="STAKEHOLDER" />
          </span>
          <StakeholderCombobox
            companyId={companyId}
            control={control}
            conversionEligible
            name={`stepOne.eventDetails.conversion.classConversionItems.${index}.stakeholder`}
            onlyWithShares
          />
        </div>
        <div className="flex w-full items-center justify-between">
          <span className="text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="FROM_SHARE_CLASS" />
          </span>
          <ShareClassCombobox
            commonStockIsAllowed={false}
            companyId={companyId}
            control={control}
            fieldName={`stepOne.eventDetails.conversion.classConversionItems.${index}.shareClassFrom`}
            stakeholderFromId={
              stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.stakeholder?.id
            }
          />
        </div>
        <div className="flex w-full items-center justify-between">
          <span className="text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="TO_SHARE_CLASS" />
          </span>
          <ShareClassCombobox
            companyId={companyId}
            control={control}
            fieldName={`stepOne.eventDetails.conversion.classConversionItems.${index}.shareClassTo`}
            shareClassFromId={
              stepOne?.eventDetails?.conversion?.classConversionItems?.[index]?.shareClassFrom?.id
            }
          />
        </div>
        <div className="flex w-full items-center justify-between">
          <span className="text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="NO_OF_SHARES" />
          </span>
          <div className="flex flex-col items-start gap-2">
            <FormInput
              control={control}
              name={`stepOne.eventDetails.conversion.classConversionItems.${index}.shares`}
              numberOnly
              placeholder={<AppFormattedMessage id="VALUE" />}
              shouldFormatNumber
              wrapperClassName="w-[224px]"
            />
            {balance && (
              <span className="text-xs text-gray-600">
                <AppFormattedMessage id="AVAILABLE" />{' '}
                <AnimatedNumber
                  integer
                  value={Math.max(calculateAvailableShares(index, false), 0)}
                />
              </span>
            )}
          </div>
        </div>
        {isShareClassDuplicate && (
          <div className="flex items-center gap-1">
            <InfoCircle />
            <span className="rounded text-xs font-[450] text-fireside-600">
              <AppFormattedMessage id="PLEASE_CHOOSE_DIFFERENT_SHARE_CLASSES" />
            </span>
          </div>
        )}
        <div className="h-[1px] w-full bg-gray-100" />
        <div className="flex w-full items-center justify-between py-3">
          <span className="text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="CONVERTED_SHARES" />
          </span>
          <div className="flex flex-col items-end gap-2">
            <span
              className={twMerge(
                'rounded px-3 py-1 text-sm font-[550]',
                getConvertedShares(index) === 0
                  ? 'bg-gray-100 text-gray-300'
                  : 'bg-brand-25 text-brand-700',
              )}
            >
              <AnimatedNumber emptyDisplay="00" integer value={getConvertedShares(index)} />
            </span>
            {balance && (
              <span className="text-xs text-gray-600">
                <AppFormattedMessage id="AVAILABLE" />{' '}
                <AnimatedNumber
                  integer
                  value={Math.max(calculateAvailableShares(index, true), 0)}
                />
              </span>
            )}
          </div>
        </div>
        <div className="h-[1px] w-full bg-gray-100" />
        <div className="flex w-full items-center justify-between py-3">
          <span className="text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="CONVERSION_RATIO" />
          </span>
          <span
            className={twMerge(
              'rounded px-3 py-1 text-sm font-[550]',
              stepOne?.eventDetails?.['conversion']?.classConversionItems?.[index]?.shareClassFrom
                ?.conversionRatio === 0
                ? 'bg-gray-100 text-gray-300'
                : 'bg-brand-25 text-brand-700',
            )}
          >
            {stepOne?.eventDetails?.['conversion']?.classConversionItems?.[index]?.shareClassFrom
              ?.conversionRatio || 0}
            x
          </span>
        </div>
      </div>
    </FormDropDown>
  );
};
